import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useSnapshot } from 'valtio';

import { StripeProduct } from '@cartwhl/common/types';
import Switch from '@cartwhl/ui/atoms/Switch';
import Text from '@cartwhl/ui/base/Text';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import Button from '@cartwhl/ui/input/Button';

import { customerStore, productStore, userStore } from '../../../stores/global';
import EnterpriseCard from './EnterpriseCard';
import PlanCard from './PlanCard';

export default function StudioPricingPage() {
  const { userInfo } = useSnapshot(userStore.state);
  const { products } = useSnapshot(productStore.state);
  const [isYearly, setIsYearly] = useState(false);
  const savings = productStore.getSavings('pro');

  useEffect(() => {
    productStore.fetchProducts();
  }, []);

  useEffect(() => {
    if (userInfo.stripeCustomerID) {
      customerStore.fetchCustomer(userInfo.stripeCustomerID);
    }
  }, [userInfo.stripeCustomerID]);

  return (
    <main className="mb-32">
      {/* Header */}
      <div className="container relative z-0 my-10 flex flex-col gap-9 lg:mb-16 lg:mt-[80px] lg:gap-12">
        <Text as="h1" variant="web/display/xl" lg:variant="web/display/3xl">
          Canvas Pricing
        </Text>
        <div className="flex justify-between">
          <Switch
            className="flex h-[42px] font-medium sm:h-[48px]"
            left="Pay monthly"
            right={savings ? `Pay yearly — ${savings}% off` : 'Pay yearly'}
            superscript={null}
            defaultValue={isYearly}
            onChange={setIsYearly}
            variant="studio/body/medium/md"
          />

          <Link to="/api-pricing" className="hidden sm:block">
            <Button className="px-10">View API plan</Button>
          </Link>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="container relative mb-4">
        {/* <div className="absolute bottom-full right-5 -z-10 w-[250px] md:hidden">
          <img
            src="/images/pricing_characters.png"
            alt="Cartwheel Characters"
          />
        </div> */}
        <div className="flex flex-col flex-wrap gap-4 sm:flex-row md:mt-8">
          {[products.free, products.pro, products.unlimited].map(
            (product, i) => (
              <PlanCard
                key={product?.id || i}
                product={product as StripeProduct | null}
                interval={isYearly ? 'year' : 'month'}
                highlighted={i === 2}
              />
            ),
          )}
          <EnterpriseCard />
        </div>
      </div>

      {/* Feature Comparison Tables */}
      <div className="container mb-[112px] mt-[112px] flex flex-col gap-14">
        <Table title="Create">
          <Row content={['Spins / mo', '150', '1,500', '4,500', 'unlimited']} />
          <Row content={['Purchase more Spins', true, true, true, true]} />
          <Row
            content={[
              'Motion library access',
              'Limited',
              'Full',
              'Full',
              'Full',
            ]}
          />
          <Row content={['Text to motion', true, true, true, true]} />
          <Row content={['Video to motion', true, true, true, true]} />
          <Row content={['Generate character', true, true, true, true]} />
        </Table>

        <Table title="Edit">
          <Row content={['Cartwheel characters', true, true, true, true]} />
          <Row content={['Upload character', true, true, true, true]} />
          <Row content={['Variable FPS', true, true, true, true]} />
          <Row content={['Motion trimming', true, true, true, true]} />
          <Row content={['Custom frame-step size', true, true, true, true]} />
        </Table>

        <Table title="Export">
          <Row content={['Video', true, true, true, true]} />
          <Row
            content={[
              '.bvh, .fbx, .glTF',
              <CheckWithLabel
                key="creator-export"
                md="(5 fr / mo)"
                lg="(5 Free / mo)"
              />,
              <CheckWithLabel
                key="pro-export"
                md="(5 fr / mo)"
                lg="(5 Free / mo)"
              />,
              true,
              true,
            ]}
          />
          <Row
            content={[
              <span key="maya">
                Maya{' '}
                <a
                  href="https://docs.getcartwheel.com/docs/integrating-cartwheel/maya-rig"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  (w/ animator-ready rig)
                </a>
              </span>,
              true,
              true,
              true,
              true,
            ]}
          />
          <Row content={['Rig 2 Rig plug-in', false, true, true, true]} />
          <Row content={['IK turned on', false, true, true, true]} />
          <Row content={['Advanced spline cleanup', false, true, true, true]} />
        </Table>

        <Table title="Support">
          <Row content={['Advanced support', false, true, true, true]} />
          <Row
            content={['Early access to new features', false, false, true, true]}
          />
          <Row content={['Team collaboration', false, false, false, true]} />
          <Row
            content={[
              'Pipeline integration support',
              false,
              false,
              false,
              true,
            ]}
          />
        </Table>
      </div>

      <Link to="/api-pricing" className="container mb-[64px] block sm:hidden">
        <Button className="w-full">View API plan</Button>
      </Link>
    </main>
  );
}

function Table({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-4 sm:gap-0">
      <header className="flex flex-col gap-8 sm:mb-3 sm:h-14 sm:flex-row sm:items-end sm:gap-0">
        <Text
          as="div"
          variant="web/display/lg"
          md:variant="web/display/xl"
          lg:variant="web/display/2xl"
          className="w-[280px] flex-none lg:w-[350px]"
        >
          {title}
        </Text>
        <div className="bg-gray-15 text-gray-90 sm:text-gray-45 dark:sm:text-gray-60 flex h-[52px] flex-none items-center rounded-2xl px-3 text-center sm:h-auto sm:flex-1 sm:rounded-none sm:bg-transparent sm:px-0 sm:text-left">
          <Text
            as="div"
            variant="web/body/medium/xs"
            sm:variant="web/body/medium/md"
            lg:variant="web/body/medium/lg"
            xl:variant="web/body/medium/24"
            className="flex-1 text-left"
          >
            Creator
          </Text>
          <Text
            as="div"
            variant="web/body/medium/xs"
            sm:variant="web/body/medium/md"
            lg:variant="web/body/medium/lg"
            xl:variant="web/body/medium/24"
            className="flex-1"
          >
            <span className="sm:hidden">Pro</span>
            <span className="hidden sm:inline">Professional</span>
          </Text>
          <Text
            as="div"
            variant="web/body/medium/xs"
            sm:variant="web/body/medium/md"
            lg:variant="web/body/medium/lg"
            xl:variant="web/body/medium/24"
            className="flex-1"
          >
            Advanced
          </Text>
          <Text
            as="div"
            variant="web/body/medium/xs"
            sm:variant="web/body/medium/md"
            lg:variant="web/body/medium/lg"
            xl:variant="web/body/medium/24"
            className="flex-1 text-right sm:text-left"
          >
            Enterprise
          </Text>
        </div>
      </header>
      {children}
    </div>
  );
}

function Row({
  content,
}: {
  content: [
    ReactNode | boolean,
    ReactNode | boolean,
    ReactNode | boolean,
    ReactNode | boolean,
    ReactNode | boolean,
  ];
}) {
  return (
    <div className="border-gray-30 dark:border-gray-60 flex flex-col gap-4 border-t pt-4 first-of-type:border-t-0 first-of-type:pt-0 sm:h-14 sm:flex-row sm:items-center sm:gap-0 sm:pt-0 sm:first-of-type:border-t sm:first-of-type:pl-0">
      <Text
        as="div"
        variant="web/body/medium/md"
        sm:variant="web/body/medium/lg"
        xl:variant="web/body/medium/24"
        className="w-[280px] flex-none pl-3 sm:pl-0 lg:w-[350px]"
      >
        {printRowContent(content[0])}
      </Text>
      <div className="text-gray-60 sm:text-gray-90 flex flex-1 text-center sm:text-left">
        <Text
          as="div"
          variant="web/body/medium/md"
          lg:variant="web/body/medium/lg"
          xl:variant="web/body/medium/24"
          className="flex-1"
        >
          {printRowContent(content[1])}
        </Text>
        <Text
          as="div"
          variant="web/body/medium/md"
          lg:variant="web/body/medium/lg"
          xl:variant="web/body/medium/24"
          className="flex-1"
        >
          {printRowContent(content[2])}
        </Text>
        <Text
          as="div"
          variant="web/body/medium/md"
          lg:variant="web/body/medium/lg"
          xl:variant="web/body/medium/24"
          className="flex-1"
        >
          {printRowContent(content[3])}
        </Text>
        <Text
          as="div"
          variant="web/body/medium/md"
          lg:variant="web/body/medium/lg"
          xl:variant="web/body/medium/24"
          className="flex-1"
        >
          {printRowContent(content[4])}
        </Text>
      </div>
    </div>
  );
}

function printRowContent(content: ReactNode | boolean) {
  if (typeof content === 'boolean' && content) {
    return <CheckmarkIcon className="text-gray-90 inline" />;
  } else if (typeof content === 'boolean' && !content) {
    return <span className="text-gray-45 dark:text-gray-60">—</span>;
  }
  return content;
}

/**
 * Shows different text at different breakpoints.
 * Useful for showing abbreviated text on mobile and full text on desktop.
 */
function ResponsiveCopy({
  text,
  sm,
  md,
  lg,
  xl,
}: {
  text: ReactNode;
  sm?: ReactNode;
  md?: ReactNode;
  lg?: ReactNode;
  xl?: ReactNode;
}) {
  const isSM = useMediaQuery('(min-width: 640px)');
  const isMD = useMediaQuery('(min-width: 768px)');
  const isLG = useMediaQuery('(min-width: 1024px)');
  const isXL = useMediaQuery('(min-width: 1440px)');

  let returnText = text;

  if (isSM && sm) returnText = sm;
  if (isMD && md) returnText = md;
  if (isLG && lg) returnText = lg;
  if (isXL && xl) returnText = xl;

  return <>{returnText}</>;
}

/**
 * Shows a checkmark with optional responsive label.
 * On smaller screens shows just the checkmark, on larger screens shows checkmark + label.
 */
function CheckWithLabel({ md, lg }: { md?: ReactNode; lg?: ReactNode }) {
  return (
    <div className="flex items-center justify-center gap-2 sm:justify-start xl:gap-4">
      <CheckmarkIcon className="text-gray-90" />
      <ResponsiveCopy text="" md={md} lg={lg} />
    </div>
  );
}
